import type { SetState } from "zustand";

import type { GetAskConversationResponseJson } from "@performyard/service-contracts";

import type {
	ChatMessage,
	FormImprovement,
	FormImprovementState,
	FormQuestionSuggestionState,
	FormQuestionSuggestionWithId,
	FormSuggestion,
	FormSuggestionState,
	GoalImprovement,
	GoalImprovementState,
	GoalSuggestion,
	GoalSuggestionState,
	ResponseImprovement,
	ResponseImprovementState,
} from "../../types";
import type { AskPerformYardState } from "../types";

import {
	clearStoredConversationId,
	getPersistedSuggestionOutcomes,
	getStoredConversationId,
	setStoredConversationId,
} from "../helpers";
import { drainPendingChunks } from "../streaming-buffer";

/**
 * The conversation the panel is currently showing (PY-13559).
 *
 * The id is server-minted: the client never invents one. On a new chat this
 * slice holds null until the `conversation` SSE event names the conversation the
 * first turn created.
 */
/**
 * A turn that is still streaming, held per conversation so switching away and
 * back does not lose it.
 *
 * The server persists the exchange when the stream finishes, so this only has
 * to cover the window before that — but that window is exactly when a user
 * toggling between chats would otherwise find the conversation they just
 * spoke in looking empty.
 */
export type PendingTurn = {
	userContent: string;
	/**
	 * The id the live stream is writing to. Reused verbatim when the thread is
	 * rebuilt on switch-back, so the stream's remaining chunks keep landing on
	 * the same bubble instead of being dropped against an unrecognized id.
	 */
	assistantMessageId: string;
	assistantContent: string;
	/**
	 * When the turn's bubble first appeared, so a rebuild reuses it instead of
	 * stamping a fresh one and making the in-flight message jump.
	 */
	startedAt: Date;
	/**
	 * The suggestion cards this turn has produced so far.
	 *
	 * Held on the turn rather than read back out of the store at rebuild time,
	 * because loading another conversation replaces `messages` and every
	 * card-state array with that conversation's — so by the time the user
	 * returns there is nothing left in state to carry forward. `pendingTurns` is
	 * the only thing keyed per conversation that a load leaves alone, which
	 * makes this the sole copy that survives the round trip. Same reasoning that
	 * already puts `assistantContent` here rather than reading the bubble back.
	 *
	 * States, not raw payloads, so a card the user acted on mid-stream comes
	 * back in the status they left it in instead of armed for a second create.
	 */
	suggestionStates: SuggestionStates;
};

/** The card payloads an SSE event carries, tagged so one action can route any of them. */
export type PendingTurnSuggestions =
	| { kind: "goals"; items: GoalSuggestion[] }
	| { kind: "goalImprovements"; items: GoalImprovement[] }
	| { kind: "forms"; items: FormSuggestion[] }
	| { kind: "formQuestions"; items: FormQuestionSuggestionWithId[] }
	| { kind: "formImprovements"; items: FormImprovement[] }
	| { kind: "responseImprovements"; items: ResponseImprovement[] };

/** `startPendingTurn` fills these in, so callers opening a turn need not. */
export type StartPendingTurnInput = Omit<PendingTurn, "suggestionStates" | "startedAt"> &
	Partial<Pick<PendingTurn, "suggestionStates" | "startedAt">>;

export type ConversationSlice = {
	conversationId: string | null;
	conversationTitle: string | null;
	/** True while a past thread is being fetched, so the panel can show a loading state instead of an empty one. */
	isLoadingConversation: boolean;
	/** In-flight turns by conversation id. Survives switching away; cleared when the turn lands. */
	pendingTurns: Record<string, PendingTurn>;
	/**
	 * Last-known card outcomes by conversation id, for the life of the tab (PY-14326).
	 *
	 * The server never records whether a suggestion was acted on (see
	 * `buildRestoredSuggestionStates`), so a cold restore is always `pending`.
	 * Snapshotted here on the way out of a conversation and overlaid back on
	 * the way in, so leaving one and coming back still shows what got done —
	 * this does not survive a page reload, only navigating within the panel.
	 */
	sessionSuggestionStates: Record<string, SuggestionStates>;

	/**
	 * Adopt the conversation named by the `conversation` SSE event.
	 *
	 * `title` is absent on the first emission for a brand-new conversation and
	 * arrives on the second, once the titling pass has run.
	 */
	setActiveConversation: (props: { conversationId: string; title?: string }) => void;
	/** Replace the thread with a restored one. */
	loadConversation: (conversation: GetAskConversationResponseJson) => void;
	setIsLoadingConversation: (value: boolean) => void;
	/** Forget the active conversation without touching the transcript (`resetChat` owns that). */
	clearActiveConversation: () => void;

	startPendingTurn: (conversationId: string, turn: StartPendingTurnInput) => void;
	appendPendingTurn: (conversationId: string, chunk: string) => void;
	/**
	 * Record cards onto the in-flight turn as they stream, whether or not the
	 * conversation is on screen. The card events fire once and are never
	 * replayed, so a turn whose cards arrive while the user is reading another
	 * thread has nowhere else to keep them.
	 */
	recordPendingTurnSuggestions: (
		conversationId: string,
		messageId: string,
		suggestions: PendingTurnSuggestions
	) => void;
	completePendingTurn: (conversationId: string) => void;
	/**
	 * Drop every in-flight record at once. A full store reset — individual
	 * teardown goes through `completePendingTurn`, because a `useAskStream`
	 * instance unmounting must not discard a turn another instance is still
	 * streaming.
	 */
	clearPendingTurns: () => void;
};

/**
 * Restored messages carry the text, its citations, and the suggestion cards the
 * turn produced.
 *
 * The cards come back so a saved conversation shows what the user asked for and
 * what they got, which is what "restored exactly as it was left" means. They
 * come back inert: `buildRestoredSuggestionStates` tags every state
 * `isRestored`, which disables the card's action in the UI and makes the store
 * action behind it refuse the card as well. Nothing here re-arms a create or an
 * apply — see `AskConversationSuggestions` in `@performyard/service-contracts`
 * for why that would be unsafe rather than merely untidy.
 */
const toChatMessages = (conversation: GetAskConversationResponseJson): ChatMessage[] =>
	conversation.messages.map((message) => ({
		id: message.id,
		role: message.role,
		content: message.content,
		...(message.sources?.length ? { sources: message.sources } : {}),
		...(message.suggestions?.goals?.length ? { goalSuggestions: message.suggestions.goals } : {}),
		...(message.suggestions?.forms?.length ? { formSuggestions: message.suggestions.forms } : {}),
		...(message.suggestions?.formQuestions?.length
			? { formQuestionSuggestions: message.suggestions.formQuestions }
			: {}),
		...(message.suggestions?.goalImprovements?.length
			? { goalImprovements: message.suggestions.goalImprovements }
			: {}),
		...(message.suggestions?.formImprovements?.length
			? { formImprovements: message.suggestions.formImprovements }
			: {}),
		...(message.suggestions?.responseImprovements?.length
			? { responseImprovements: message.suggestions.responseImprovements }
			: {}),
		timestamp: new Date(message.createdAt),
	}));

/** The six card-state arrays the suggestion containers read, moved around as one unit. */
export type SuggestionStates = {
	goalSuggestionStates: GoalSuggestionState[];
	goalImprovementStates: GoalImprovementState[];
	formSuggestionStates: FormSuggestionState[];
	formQuestionSuggestionStates: FormQuestionSuggestionState[];
	formImprovementStates: FormImprovementState[];
	responseImprovementStates: ResponseImprovementState[];
};

const emptySuggestionStates = (): SuggestionStates => ({
	goalSuggestionStates: [],
	goalImprovementStates: [],
	formSuggestionStates: [],
	formQuestionSuggestionStates: [],
	formImprovementStates: [],
	responseImprovementStates: [],
});

/** Which array of `SuggestionStates` each card event's payload belongs in. */
const SUGGESTION_STATE_KEY = {
	goals: "goalSuggestionStates",
	goalImprovements: "goalImprovementStates",
	forms: "formSuggestionStates",
	formQuestions: "formQuestionSuggestionStates",
	formImprovements: "formImprovementStates",
	responseImprovements: "responseImprovementStates",
} as const satisfies Record<PendingTurnSuggestions["kind"], keyof SuggestionStates>;

/**
 * The per-card state the containers read, rebuilt from a restored thread.
 *
 * Status is always `pending` here because the server-persisted thread does
 * not record whether the user ever acted on a suggestion — this is a cold
 * restore. `mergeKnownOutcomes` is what overlays a real status back on when
 * this session already knows better (PY-14326). `isRestored` is what makes
 * the card inert regardless of status.
 */
const buildRestoredSuggestionStates = (messages: ChatMessage[]): SuggestionStates => {
	const states: SuggestionStates = emptySuggestionStates();

	for (const message of messages) {
		const restored = { status: "pending" as const, messageId: message.id, isRestored: true };

		for (const suggestion of message.goalSuggestions ?? []) {
			states.goalSuggestionStates.push({ suggestion, ...restored });
		}
		for (const suggestion of message.goalImprovements ?? []) {
			states.goalImprovementStates.push({ suggestion, ...restored });
		}
		for (const suggestion of message.formSuggestions ?? []) {
			states.formSuggestionStates.push({ suggestion, ...restored });
		}
		for (const suggestion of message.formQuestionSuggestions ?? []) {
			states.formQuestionSuggestionStates.push({ suggestion, ...restored });
		}
		for (const suggestion of message.formImprovements ?? []) {
			states.formImprovementStates.push({ suggestion, ...restored });
		}
		for (const suggestion of message.responseImprovements ?? []) {
			states.responseImprovementStates.push({ suggestion, ...restored });
		}
	}

	return states;
};

/**
 * Overlay durable, localStorage-recorded outcomes onto freshly restored (always-
 * `pending`) ubiquitous cards, so a goal/form already created or a question already
 * added still shows that way — and stays inert — even after a full reload or in a
 * brand-new tab, where `sessionSuggestionStates` below has nothing (PY-14326 follow-up).
 * Coaching cards don't need this: they're never actionable once restored regardless
 * of status (see ImprovementCard).
 */
const overlayPersistedOutcomes = (restored: SuggestionStates): SuggestionStates => {
	const outcomes = getPersistedSuggestionOutcomes();
	if (Object.keys(outcomes).length === 0) return restored;

	return {
		...restored,
		goalSuggestionStates: restored.goalSuggestionStates.map((gs) => {
			const outcome = outcomes[gs.suggestion.id];
			return outcome?.kind === "goal"
				? { ...gs, status: "created" as const, createdGoalId: outcome.createdGoalId }
				: gs;
		}),
		formSuggestionStates: restored.formSuggestionStates.map((fs) => {
			const outcome = outcomes[fs.suggestion.id];
			return outcome?.kind === "form"
				? {
						...fs,
						status: "created" as const,
						createdFormId: outcome.createdFormId,
						createdFormBaseId: outcome.createdFormBaseId,
					}
				: fs;
		}),
		formQuestionSuggestionStates: restored.formQuestionSuggestionStates.map((fs) => {
			const outcome = outcomes[fs.suggestion.id];
			return outcome?.kind === "formQuestion" ? { ...fs, status: "added" as const } : fs;
		}),
		// Keeps Figma's "History / One Applied" — the confirmation, never the action.
		responseImprovementStates: restored.responseImprovementStates.map((rs) => {
			const outcome = outcomes[rs.suggestion.id];
			return outcome?.kind === "responseImprovement" ? { ...rs, status: "applied" as const } : rs;
		}),
	};
};

type CardState = { suggestion: { id: string }; messageId: string; isRestored?: boolean };

/**
 * Fold the card states currently in the store back onto the turn that owns them.
 *
 * Run against the conversation being *left*, because that is the last moment its
 * live arrays exist: the load about to happen replaces all five with the
 * incoming thread's. The store copy wins where both have a card, since it is the
 * one carrying status the user produced — a card they created mid-stream must not
 * come back armed for a second create. Cards recorded while the conversation was
 * off screen have no store copy at all and are kept as they were.
 */
const snapshotLiveSuggestionStates = (state: AskPerformYardState, pending: PendingTurn): SuggestionStates => {
	const merge = <StateType extends CardState>(recorded: StateType[], storeStates: StateType[]): StateType[] => {
		const live = storeStates.filter(
			(cardState) => !cardState.isRestored && cardState.messageId === pending.assistantMessageId
		);
		const liveIds = new Set(live.map((cardState) => cardState.suggestion.id));
		return [...recorded.filter((cardState) => !liveIds.has(cardState.suggestion.id)), ...live];
	};

	const recorded = pending.suggestionStates;

	return {
		goalSuggestionStates: merge(recorded.goalSuggestionStates, state.goalSuggestionStates),
		goalImprovementStates: merge(recorded.goalImprovementStates, state.goalImprovementStates),
		formSuggestionStates: merge(recorded.formSuggestionStates, state.formSuggestionStates),
		formQuestionSuggestionStates: merge(recorded.formQuestionSuggestionStates, state.formQuestionSuggestionStates),
		formImprovementStates: merge(recorded.formImprovementStates, state.formImprovementStates),
		responseImprovementStates: merge(recorded.responseImprovementStates, state.responseImprovementStates),
	};
};

/** The five live state arrays as they currently stand, independent of any pending turn. */
const currentSuggestionStates = (state: AskPerformYardState): SuggestionStates => ({
	goalSuggestionStates: state.goalSuggestionStates,
	goalImprovementStates: state.goalImprovementStates,
	formSuggestionStates: state.formSuggestionStates,
	formQuestionSuggestionStates: state.formQuestionSuggestionStates,
	formImprovementStates: state.formImprovementStates,
	responseImprovementStates: state.responseImprovementStates,
});

/**
 * Overlay a conversation's last-known-this-session outcome onto its freshly
 * restored (always-`pending`) cards, matched by suggestion id (PY-14326).
 *
 * `known` came from `currentSuggestionStates` on a previous visit, so it may
 * carry a card the restored list no longer has (deleted server-side between
 * visits) or miss one the restored list just gained (added after); `overlay`
 * only ever touches ids present in both.
 */
const mergeKnownOutcomes = (restored: SuggestionStates, known: SuggestionStates | undefined): SuggestionStates => {
	if (!known) return restored;

	const overlay = <StateType extends { suggestion: { id: string } }>(
		restoredList: StateType[],
		knownList: StateType[]
	): StateType[] => {
		const knownById = new Map(knownList.map((cardState) => [cardState.suggestion.id, cardState]));
		return restoredList.map((cardState) => {
			const match = knownById.get(cardState.suggestion.id);
			return match ? { ...cardState, ...match, isRestored: true } : cardState;
		});
	};

	return {
		goalSuggestionStates: overlay(restored.goalSuggestionStates, known.goalSuggestionStates),
		goalImprovementStates: overlay(restored.goalImprovementStates, known.goalImprovementStates),
		formSuggestionStates: overlay(restored.formSuggestionStates, known.formSuggestionStates),
		formQuestionSuggestionStates: overlay(
			restored.formQuestionSuggestionStates,
			known.formQuestionSuggestionStates
		),
		formImprovementStates: overlay(restored.formImprovementStates, known.formImprovementStates),
		responseImprovementStates: overlay(restored.responseImprovementStates, known.responseImprovementStates),
	};
};

/**
 * Add the in-flight turn's own card states on top of the ones rebuilt from saved
 * history.
 *
 * These are the armed cards, and they come from the turn's own record rather
 * than from the store: the card events fire once and are never replayed, so a
 * turn that emitted cards before the user switched away — or while they were
 * away — has no other surviving copy. Dropping them would blank the card for the
 * rest of the stream and then bring it back inert once the turn is saved, so the
 * user would never get to act on it.
 */
const withLiveSuggestionStates = (
	restoredStates: SuggestionStates,
	pending: PendingTurn | undefined
): SuggestionStates => {
	if (!pending) return restoredStates;

	const live = pending.suggestionStates;

	return {
		goalSuggestionStates: [...restoredStates.goalSuggestionStates, ...live.goalSuggestionStates],
		goalImprovementStates: [...restoredStates.goalImprovementStates, ...live.goalImprovementStates],
		formSuggestionStates: [...restoredStates.formSuggestionStates, ...live.formSuggestionStates],
		formQuestionSuggestionStates: [
			...restoredStates.formQuestionSuggestionStates,
			...live.formQuestionSuggestionStates,
		],
		formImprovementStates: [...restoredStates.formImprovementStates, ...live.formImprovementStates],
		responseImprovementStates: [...restoredStates.responseImprovementStates, ...live.responseImprovementStates],
	};
};

/**
 * The card arrays for the live bubble, taken from the same record the states
 * come from so the message and the states cannot disagree about what the turn
 * produced. Empty kinds are left off rather than set to `[]`, which is what the
 * containers use to decide there is no card block to render.
 */
const liveMessageSuggestions = (pending: PendingTurn): Partial<ChatMessage> => {
	const live = pending.suggestionStates;
	const suggestionsOf = <StateType extends { suggestion: SuggestionType }, SuggestionType>(
		states: StateType[]
	): SuggestionType[] => states.map((cardState) => cardState.suggestion);

	return {
		...(live.goalSuggestionStates.length ? { goalSuggestions: suggestionsOf(live.goalSuggestionStates) } : {}),
		...(live.goalImprovementStates.length ? { goalImprovements: suggestionsOf(live.goalImprovementStates) } : {}),
		...(live.formSuggestionStates.length ? { formSuggestions: suggestionsOf(live.formSuggestionStates) } : {}),
		...(live.formQuestionSuggestionStates.length
			? { formQuestionSuggestions: suggestionsOf(live.formQuestionSuggestionStates) }
			: {}),
		...(live.formImprovementStates.length ? { formImprovements: suggestionsOf(live.formImprovementStates) } : {}),
		...(live.responseImprovementStates.length
			? { responseImprovements: suggestionsOf(live.responseImprovementStates) }
			: {}),
	};
};

export const createConversationSlice = (set: SetState<AskPerformYardState>) => ({
	// Read once at store creation so a full page reload lands back in the
	// conversation the user was in, rather than silently starting a new one.
	conversationId: getStoredConversationId(),
	conversationTitle: null as string | null,
	isLoadingConversation: false,
	pendingTurns: {} as Record<string, PendingTurn>,
	sessionSuggestionStates: {} as Record<string, SuggestionStates>,

	setActiveConversation: ({ conversationId, title }: { conversationId: string; title?: string }) => {
		setStoredConversationId(conversationId);
		set((state) => ({
			conversationId,
			// No title yet means "not titled yet", not "clear the title" — the
			// header keeps showing "Ask PerformYard" and swaps once, when the
			// generated title lands on the second emission.
			conversationTitle: title !== undefined ? title : state.conversationTitle,
		}));
	},

	loadConversation: (conversation: GetAskConversationResponseJson) => {
		setStoredConversationId(conversation.id);
		set((state) => {
			const restored = toChatMessages(conversation);

			// A turn sent in this conversation may still be streaming — the server
			// only persists once it finishes, so the fetch above cannot see it.
			// Re-appending it is what lets a user send, switch away, and come back
			// to a conversation that is still visibly working rather than one that
			// has apparently forgotten what they just said.
			// The conversation being left may have a turn still streaming, and the
			// statuses of its cards live only in the arrays this load is about to
			// overwrite. Fold them onto that turn's own record first — nothing else
			// here touches another conversation's entry, so this is the last chance.
			//
			// Runs even when the load is a reload of the conversation already on
			// screen, which Recent Conversations neither filters nor disables: the
			// arrays are replaced either way, so skipping it there would rebuild a
			// card the user created from the record's own `pending` and arm it for a
			// second create.
			const outgoingId = state.conversationId;

			// Whatever the outgoing conversation's cards look like right now — its own
			// restored cards plus anything acted on this visit — becomes what the next
			// visit overlays onto a cold restore (PY-14326). Keyed on the way out rather
			// than read back on the way in, for the same reason `snapshotLiveSuggestionStates`
			// runs here: this is the last moment those arrays hold this conversation's data.
			const sessionSuggestionStates = outgoingId
				? { ...state.sessionSuggestionStates, [outgoingId]: currentSuggestionStates(state) }
				: state.sessionSuggestionStates;

			const outgoing = outgoingId ? state.pendingTurns[outgoingId] : undefined;
			const pendingTurns =
				outgoingId && outgoing
					? {
							...state.pendingTurns,
							[outgoingId]: {
								...outgoing,
								suggestionStates: snapshotLiveSuggestionStates(state, outgoing),
							},
						}
					: state.pendingTurns;

			const pending = pendingTurns[conversation.id];

			// Anything still buffered for this turn is already counted in
			// `assistantContent`, which the stream appends to unconditionally.
			// Dropping it here stops the next animation frame from flushing that
			// same text a second time onto the rebuilt bubble.
			if (pending) drainPendingChunks(pending.assistantMessageId);

			const messages = pending
				? [
						...restored,
						{
							id: `${pending.assistantMessageId}-user`,
							role: "user" as const,
							content: pending.userContent,
							timestamp: new Date(),
						},
						{
							// Built entirely from the turn's record. Reading the bubble back
							// out of `state.messages` is what the earlier version did, and it
							// cannot work: this load replaces that array, so on the way back
							// from another conversation the lookup misses and the bubble
							// silently returns without whatever it was carrying.
							//
							// Competency markers are the one thing the record does not track,
							// so a turn mid-competency-intro loses its upload affordance on a
							// rebuild — the same scope line the PR draws around competency
							// state, which has its own save lifecycle.
							id: pending.assistantMessageId,
							role: "assistant" as const,
							content: pending.assistantContent,
							...liveMessageSuggestions(pending),
							isStreaming: true,
							timestamp: pending.startedAt,
						},
					]
				: restored;

			return {
				conversationId: conversation.id,
				conversationTitle: conversation.title,
				pendingTurns,
				sessionSuggestionStates,
				messages,
				isLoadingConversation: false,
				// Clearing these matters because switching conversations mid-error
				// would otherwise carry the previous thread's error banner onto an
				// unrelated conversation. A conversation with a turn still in flight
				// keeps its streaming status instead.
				status: pending ? ("streaming" as const) : ("idle" as const),
				streamStage: null,
				streamMessage: null,
				error: null,
				// The replayed thread's cards are inert; the in-flight turn's own
				// cards are armed like any other live turn's, whether they arrived
				// before this rebuild or land after it — it is the thread being
				// replayed that is inert, not the conversation.
				...withLiveSuggestionStates(
					mergeKnownOutcomes(
						overlayPersistedOutcomes(buildRestoredSuggestionStates(restored)),
						sessionSuggestionStates[conversation.id]
					),
					pending
				),
			};
		});
	},

	setIsLoadingConversation: (value: boolean) => set({ isLoadingConversation: value }),

	clearActiveConversation: () => {
		clearStoredConversationId();
		set({ conversationId: null, conversationTitle: null, isLoadingConversation: false });
	},

	startPendingTurn: (conversationId: string, turn: StartPendingTurnInput) =>
		set((state) => ({
			pendingTurns: {
				...state.pendingTurns,
				[conversationId]: {
					...turn,
					suggestionStates: turn.suggestionStates ?? emptySuggestionStates(),
					startedAt: turn.startedAt ?? new Date(),
				},
			},
		})),

	recordPendingTurnSuggestions: (conversationId: string, messageId: string, suggestions: PendingTurnSuggestions) =>
		set((state) => {
			const existing = state.pendingTurns[conversationId];
			// Same reasoning as `appendPendingTurn`: no record means the turn already
			// landed, and a late event must not resurrect it.
			if (!existing) return { pendingTurns: state.pendingTurns };

			// One card event carries the whole set for its kind, so it replaces
			// rather than appends — matching what the slice setters do to the store.
			const states = suggestions.items.map((suggestion) => ({
				suggestion,
				status: "pending" as const,
				messageId,
			}));

			return {
				pendingTurns: {
					...state.pendingTurns,
					[conversationId]: {
						...existing,
						suggestionStates: {
							...existing.suggestionStates,
							[SUGGESTION_STATE_KEY[suggestions.kind]]: states,
						},
					},
				},
			};
		}),

	appendPendingTurn: (conversationId: string, chunk: string) =>
		set((state) => {
			const existing = state.pendingTurns[conversationId];
			// No record means the turn already completed; a late chunk must not
			// resurrect it as a permanently-streaming ghost.
			if (!existing) return { pendingTurns: state.pendingTurns };
			return {
				pendingTurns: {
					...state.pendingTurns,
					[conversationId]: { ...existing, assistantContent: existing.assistantContent + chunk },
				},
			};
		}),

	completePendingTurn: (conversationId: string) =>
		set((state) => {
			if (!state.pendingTurns[conversationId]) return { pendingTurns: state.pendingTurns };
			const { [conversationId]: _completed, ...rest } = state.pendingTurns;
			return { pendingTurns: rest };
		}),

	// The store outlives the hook, so an aborted stream would otherwise leave a
	// record no one will ever complete — and reopening that conversation would
	// show a bubble stuck streaming forever.
	clearPendingTurns: () => set({ pendingTurns: {} }),
});
